import { createContext, useCallback, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/*
 * Opening a conversation from a page (plan phase 8): in the chat drawer where
 * Layout offers one (components/chat/ChatDrawer.js, ?chat=<channel id>), so
 * the page stays where it was underneath and Back returns to it; anywhere
 * else -- Field Mode, which has no Layout, or the /chat page itself -- on the
 * /chat page (?c=<channel id>), as before. Kept apart from ChatDrawer.js so a
 * page that only opens a conversation does not load the chat.
 *
 *   const openChat = useOpenChat();
 *   const { data } = await api.post('/chat/dms', { user_id });
 *   openChat(data.channel_id);
 */
export const CHAT_INBOX = 'inbox';
const CHANNEL_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Whether ?chat=<value> opens the drawer: the inbox, or a channel's id. */
export const chatTarget = (value) => value === CHAT_INBOX || CHANNEL_ID.test(value || '');

/** Layout's opener of its drawer, `(channelId) => void`; null where there is none. */
export const ChatDrawerContext = createContext(null);

export default function useOpenChat() {
  const drawer = useContext(ChatDrawerContext);
  const navigate = useNavigate();
  // The opener of the latest render: a conversation is often opened after an
  // answer (POST /chat/dms), by which time the page's address may have moved on.
  const latest = useRef(drawer);
  latest.current = drawer;
  return useCallback((channelId) => {
    const show = latest.current;
    if (show && chatTarget(channelId)) show(channelId);
    else navigate(`/chat?c=${encodeURIComponent(channelId)}`);
  }, [navigate]);
}
